/**
 * Signal traceability — the credibility distribution on the cycle report
 * (SIG-5).
 *
 * The moment it catches (02B §5): "The cycle is over — did our changes
 * trace to real signals, and how fast did we answer them?"
 *
 * Facts of the record, no verdicts: the counts come from the
 * signal_traceability_distribution view (the one truth source). L5's
 * standard applies — this section is what makes the next cycle's
 * commitments believable. Renders nothing when the signal_response
 * module is off or there is no exposed decision to report.
 */

import { useEffect, useState } from 'react';
import { Radio } from 'lucide-react';
import { useModuleGate } from '../../lib/moduleGate';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import {
  getSignalTraceability,
  type SignalTraceabilityRow,
} from '../../lib/signalEventsService';

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div>
      <div className={`text-xl font-semibold ${accent ?? 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'}`}>
        {value}
      </div>
      <div className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
        {label}
      </div>
    </div>
  );
}

export function SignalTraceabilitySection({ cycleId }: { cycleId: string }) {
  const enabled = useModuleGate('signal_response');
  const [row, setRow] = useState<SignalTraceabilityRow | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return;
    let cancelled = false;
    getSignalTraceability(workspaceId, cycleId)
      .then((r) => {
        if (!cancelled) setRow(r);
      })
      .catch(() => {
        // Absent read model is a silent no-render — omit the section rather
        // than invent numbers (L1: no basis means say nothing).
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, cycleId]);

  if (!enabled || !row || !row.exposed_total) return null;

  const pct = row.on_signal_pct !== null ? `${row.on_signal_pct}%` : 'no basis';
  const latency =
    row.avg_answer_days !== null ? `${Number(row.avg_answer_days).toFixed(1)}d` : '—';

  return (
    <section className="border border-[var(--rule)] dark:border-[var(--rule)] p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Radio className="h-4 w-4 text-[var(--text-ter)]" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          Signal traceability
        </h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Changes on signal" value={String(row.on_signal_count ?? 0)} accent="text-primary-700" />
        <Stat label="Ahead of signal" value={String(row.ahead_of_signal_count ?? 0)} />
        <Stat
          label="Unsignaled"
          value={String(row.unsignaled_count ?? 0)}
          accent={row.unsignaled_count ? 'text-luminous-coral-700' : undefined}
        />
        <Stat label="Avg answer time" value={latency} />
      </div>
      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        {pct} of this cycle&apos;s course-changes trace to a confirmed signal. Facts of the
        record — exposure is derived from signal linkage, never chosen.
      </p>
    </section>
  );
}
